import { groq } from "next-sanity"

import { getEmployeeReference } from "@/graphql/schemas/shared/utils/getEmployeeReference"
import { AppSettings } from "@/settings"

import { checkPermissions, validateAccessRightsToCustomer } from "../../shared/permissions/permissions"
import { DomainObjects, Operations } from "../../shared/permissions/permissionsConfig"
import {
  createAttachmentReferences,
  deleteAttachmentReferences,
  getOccasionReferences,
  getUpdatedAttachments,
  hasAttachmentsChanged,
} from "../utils/visitReportHelpers"
import { getVisitReportSchema } from "../utils/visitReportValidation"

export const mutationUpdateVisitReport = {
  async updateVisitReport(parent, args, context) {
    const { lang = AppSettings.defaultLocale(), id, values, cdn = false } = args

    const { user } = context

    await checkPermissions({
      user,
      domainObject: DomainObjects.VISIT_REPORTS,
      operation: Operations.UPDATE,
    })

    try {
      await getVisitReportSchema().validate(values)
    } catch (error) {
      throw new Error(error.message)
    }

    const config = AppSettings.sanityConfigByLang(lang)
    const client = context.sanityClient
    client.config({ ...config, useCdn: cdn })

    const modifiedBy = await getEmployeeReference({
      employeeNumber: user.employeeNumber,
      sanityClient: client,
    })

    const draftsFilter = `&& !(_id in path("drafts.**"))`

    // TODO: EINZA-896 generic function for type and draft check in mutations
    const visitReport = await client.fetch(
      groq`*[_type=="visitReport" && _id==$id ${draftsFilter}][0] {
       "id": _id,
       customerNumber,
        "attachmentsLength": count(attachments),
        attachments[]{"id": _key, name, size, mimetype, base64String},
      }`,
      { id: id },
    )

    await validateAccessRightsToCustomer({
      user: user,
      customerNumber: visitReport.customerNumber,
    })

    if (!visitReport) return null

    const updatedOccasions = await getOccasionReferences(client, values?.occasions)

    const attachmentReferences = await createAttachmentReferences(values.attachmentReferences, client)
    const { deletedAttachmentReferences, ...rest } = values

    const patch = {
      ...rest,
      modifiedAt: new Date().toISOString(),
      ...(modifiedBy && { modifiedBy }),
      occasions: updatedOccasions,
      modificationSource: "Sanity",
      attachmentReferences: attachmentReferences || [],
    }

    if (hasAttachmentsChanged(visitReport.attachmentsLength, values?.attachments)) {
      patch.attachments = getUpdatedAttachments(visitReport?.attachments, values?.attachments)
    } else {
      delete patch.attachments
    }

    const jsonString = JSON.stringify(patch)
    const encoder = new TextEncoder()
    const byteArray = encoder.encode(jsonString)

    // request body can only be 4MB so if it is bigger we stop the change
    if (byteArray.length > 4_000_000) {
      return null
    }

    const visitReportPatch = await client
      .patch(id)
      .set({
        ...patch,
      })
      .commit()

    if (!visitReportPatch) return null

    // delete attachment references now
    if (deletedAttachmentReferences && deletedAttachmentReferences.length > 0) {
      await deleteAttachmentReferences(values.deletedAttachmentReferences, client)
    }

    return {
      ...visitReportPatch,
      id: visitReportPatch._id,
      rev: visitReportPatch._rev,
      attachments: visitReportPatch.attachments.map((attachment) => {
        return {
          id: attachment._key,
          size: attachment.size,
          name: attachment.name,
        }
      }),
    }
  },
}
